// The synthesized call sounds of ios/Sources/Calls/CallTones.swift, as pure sample functions (the
// page renders them into WebAudio buffers): the apps ship no audio assets, so neither does the web.
//  - RINGBACK while alone on a connected call: 425 Hz, 1 s on / 4 s off (Android TONE_SUP_RINGTONE);
//  - INCOMING ring: 440 + 480 Hz, 2 s on / 4 s off;
//  - join / leave CHIMES: two notes with a soft 8 ms attack and an exponential decay, so they
//    never click — rising (E5 → A5) on join, falling (A5 → D5) on leave.

export interface TonePattern {
  frequencies: number[];
  onSeconds: number;
  offSeconds: number;
  amplitude: number;
}

export const CallTones = {
  ringback: { frequencies: [425], onSeconds: 1.0, offSeconds: 4.0, amplitude: 0.18 } as TonePattern,
  incomingRing: { frequencies: [440, 480], onSeconds: 2.0, offSeconds: 4.0, amplitude: 0.4 } as TonePattern,

  /** One sample of a looping pattern at absolute frame `frame` (CallTonePlayer's render block). */
  toneSample(pattern: TonePattern, frame: number, sampleRate: number): number {
    const cycleFrames = Math.floor((pattern.onSeconds + pattern.offSeconds) * sampleRate);
    const onFrames = Math.floor(pattern.onSeconds * sampleRate);
    const position = frame % cycleFrames;
    if (position >= onFrames) return 0;
    const time = position / sampleRate;
    let sample = 0;
    for (const f of pattern.frequencies) sample += Math.sin(2 * Math.PI * f * time);
    return (sample * pattern.amplitude) / pattern.frequencies.length;
  },

  /** Frames in one full cycle (the loop length of the rendered buffer). */
  cycleFrames(pattern: TonePattern, sampleRate: number): number {
    return Math.floor((pattern.onSeconds + pattern.offSeconds) * sampleRate);
  },

  chimeNotes(kind: 'join' | 'leave'): Array<{ frequency: number; seconds: number }> {
    return kind === 'join'
      ? [
          { frequency: 659.25, seconds: 0.11 },
          { frequency: 880, seconds: 0.18 },
        ]
      : [
          { frequency: 880, seconds: 0.11 },
          { frequency: 587.33, seconds: 0.18 },
        ];
  },

  /** The whole chime, rendered (CallChimePlayer's render block). */
  chimeSamples(kind: 'join' | 'leave', sampleRate: number): Float32Array {
    const notes = CallTones.chimeNotes(kind);
    const noteFrames = notes.map((n) => Math.floor(n.seconds * sampleRate));
    const total = noteFrames.reduce((a, b) => a + b, 0);
    const out = new Float32Array(total);
    const amplitude = 0.12;
    const attackFrames = Math.floor(0.008 * sampleRate);
    let start = 0;
    notes.forEach((note, index) => {
      const length = noteFrames[index];
      for (let local = 0; local < length; local++) {
        const time = local / sampleRate;
        const attack = Math.min(1, local / Math.max(1, attackFrames));
        const decay = Math.exp((-4.0 * local) / length);
        out[start + local] = Math.sin(2 * Math.PI * note.frequency * time) * amplitude * attack * decay;
      }
      start += length;
    });
    return out;
  },

  /** Join/leave chimes never more than one per 0.6 s — several joining at once chime once. */
  chimeGapMillis: 600,
};
